import React, { useEffect } from 'react';

function JobDescriptionModal({ isOpen, onClose, job }) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !job) return null;

  const headerOffset = 96; // Account for fixed header
  const applyEmail = job.applyEmail || 'jobs@clariti.ai';
  const renderListItem = (item, index) => {
    const isSubBullet = item.startsWith('o ');
    const displayText = isSubBullet ? item.slice(2) : item;
    return (
      <li
        key={index}
        style={{
          marginBottom: '10px',
          listStyleType: isSubBullet ? 'circle' : 'disc',
          marginLeft: isSubBullet ? '20px' : 0,
        }}
      >
        {displayText}
      </li>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="job-modal-backdrop"
        style={{
          position: 'fixed',
          top: `${headerOffset}px`,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <div
        className="job-modal"
        style={{
          position: 'fixed',
          top: `${headerOffset + 20}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90vw',
          maxWidth: '800px',
          height: `calc(100vh - ${headerOffset + 40}px)`,
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
          zIndex: 9999,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '28px 32px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', color: '#0f172a' }}>
              {job.title}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#06b6d4' }}>
                {job.department}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {(() => {
                  // Split location by em dash or regular dash to separate base location from details
                  const locationParts = job.location.split(/[–—]/).map(part => part.trim());
                  const baseLocation = locationParts[0] || job.location;
                  const locationDetails = locationParts[1] || null;
                  
                  return (
                    <>
                      <span style={{ fontSize: '0.95rem', color: '#64748b' }}>
                        {baseLocation}
                      </span>
                      {locationDetails && (
                        <>
                          <span style={{ fontSize: '0.95rem', color: '#64748b', opacity: 0.6, margin: '0 4px' }}>•</span>
                          <span style={{ fontSize: '0.95rem', color: '#64748b' }}>
                            {locationDetails}
                          </span>
                        </>
                      )}
                      {job.employmentType && (
                        <>
                          <span style={{ fontSize: '0.95rem', color: '#64748b', opacity: 0.6, margin: '0 4px' }}>•</span>
                          <span style={{ fontSize: '0.95rem', color: '#64748b' }}>
                            {job.employmentType === 'full-time' && 'Full time'}
                            {job.employmentType === 'part-time' && `Part time${job.employmentDetails ? ` (${job.employmentDetails})` : ''}`}
                            {job.employmentType === 'contractor' && 'Contractor'}
                            {job.employmentType === 'internship' && `Internship${job.employmentDetails ? ` (${job.employmentDetails})` : ''}`}
                          </span>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close job description"
            style={{
              width: '36px',
              height: '36px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#334155" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13" />
              <line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '32px',
          }}
          className="job-modal-content"
        >
          <p style={{ fontSize: '1.1rem', color: '#334155', lineHeight: '1.7', marginBottom: '28px', whiteSpace: 'pre-line' }}>
            {job.description}
          </p>

          {job.responsibilities && job.responsibilities.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '1.25rem', color: '#0f172a', fontWeight: 600 }}>
                {job.responsibilitiesHeading || 'What you\'ll do'}
              </h3>
              <ul style={{ margin: 0, paddingLeft: '24px', color: '#475569', lineHeight: '1.8' }}>
                {job.responsibilities.map((item, index) => renderListItem(item, index))}
              </ul>
            </div>
          )}

          {job.requirements && job.requirements.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '1.25rem', color: '#0f172a', fontWeight: 600 }}>
                {job.requirementsHeading || 'What we\'re looking for'}
              </h3>
              <ul style={{ margin: 0, paddingLeft: '24px', color: '#475569', lineHeight: '1.8' }}>
                {job.requirements.map((item, index) => renderListItem(item, index))}
              </ul>
            </div>
          )}

          {job.desirables && job.desirables.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '1.25rem', color: '#0f172a', fontWeight: 600 }}>
                {job.desirablesHeading || 'Desirables'}
              </h3>
              <ul style={{ margin: 0, paddingLeft: '24px', color: '#475569', lineHeight: '1.8' }}>
                {job.desirables.map((item, index) => renderListItem(item, index))}
              </ul>
            </div>
          )}

          {job.benefits && job.benefits.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '1.25rem', color: '#0f172a', fontWeight: 600 }}>
                What we offer
              </h3>
              <ul style={{ margin: 0, paddingLeft: '24px', color: '#475569', lineHeight: '1.8' }}>
                {job.benefits.map((item, index) => renderListItem(item, index))}
              </ul>
            </div>
          )}

          {job.additionalInfo && (
            <div style={{ marginBottom: '32px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
              <p style={{ margin: 0, fontSize: '0.95rem', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                {job.additionalInfo}
              </p>
            </div>
          )}

          {/* How to Apply Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.25rem', color: '#0f172a', fontWeight: 600 }}>
              How to apply
            </h3>
            <p style={{ margin: 0, fontSize: '1rem', color: '#475569', lineHeight: '1.7', whiteSpace: 'pre-line' }}>
              {job.applyInstructions ? (
                job.applyInstructions
              ) : (
                <>
                  Click <strong>"Apply now"</strong> below, or email <a href={`mailto:${applyEmail}`} style={{ color: '#06b6d4', fontWeight: 600 }}>{applyEmail}</a> with your CV and a few quick words (maximum 200) on what interests you about this role or Clariti AI.
                </>
              )}
            </p>
          </div>
        </div>

        {/* Footer with Apply Button */}
        <div
          style={{
            padding: '24px 32px',
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
            flexShrink: 0,
          }}
        >
          <a
            href={`mailto:${applyEmail}?subject=${encodeURIComponent(job.applySubject || `Application: ${job.title}`)}`}
            className="btn btn-primary"
            style={{ width: '100%', textAlign: 'center', display: 'block' }}
          >
            Apply now
          </a>
        </div>
      </div>
    </>
  );
}

export default JobDescriptionModal;

